"use client";

import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Upload, CheckCircle2, AlertTriangle, Star } from "lucide-react";
import {
	studentImportApi,
	type StudentImportCommitResult,
	type StudentImportDecisions,
	type StudentImportPreview,
	type HighSchoolNameDecisions,
	type HighSchoolNameConflict,
} from "@/lib/student-import-api.service";
import { Toast, getSafeErrorMessage, cn } from "@/lib/utils";
import { DiffText } from "./diff-text";

/** Tên sẽ được ghi cho một mã trường: lựa chọn của admin, hoặc mặc định ứng viên nhiều SV nhất. */
function selectedHighSchoolName(
	conflict: HighSchoolNameConflict,
	decisions: HighSchoolNameDecisions,
): string {
	return decisions[`${conflict.system}:${conflict.code}`] ?? conflict.options[0].name;
}

/**
 * Mã này sẽ bị ĐỔI TÊN trong danh mục. Một định nghĩa duy nhất cho cả ba nơi
 * đọc nó — màu nút, cảnh báo trong bảng, và hộp xác nhận. Tính lại ở từng chỗ
 * là cách chúng trôi khỏi nhau: bản trước thiếu đúng vế `currentName !== null`
 * và tô cam cả những mã trường mới, thứ không có gì để đổi tên.
 */
function willRenameHighSchool(
	conflict: HighSchoolNameConflict,
	decisions: HighSchoolNameDecisions,
): boolean {
	return (
		conflict.currentName !== null &&
		selectedHighSchoolName(conflict, decisions) !== conflict.currentName
	);
}

interface NameChoiceButtonProps {
	name: string;
	/** Số SV trong file viết đúng cách này. `0` = tên chỉ có trong danh mục. */
	students: number;
	/** Tên này đang nằm trong danh mục `high_school`. */
	isCurrent: boolean;
	/** Ứng viên nhiều SV nhất — cái được chọn sẵn. Chỉ đánh dấu khi có hơn một ứng viên. */
	isTop: boolean;
	selected: boolean;
	/** Đang chọn và sẽ thay tên trong danh mục — tô cam cảnh báo. */
	willOverwrite: boolean;
	onSelect: () => void;
}

/**
 * Một cách viết tên để admin chọn. Ba vai một tên có thể mang được mã hoá bằng
 * hình chứ không bằng chữ, vì nhãn chữ lặp lại trên từng nút của từng dòng làm
 * bảng không đọc nổi: **đậm** = đang trong danh mục, ★ = ứng viên nhiều SV nhất,
 * số = số SV trong file. Chú thích nằm một lần phía trên bảng.
 *
 * Đậm và icon là hai thứ giữ được độ tương phản trên CẢ ba nền nút (trắng, đen,
 * cam) — dùng màu chữ riêng ở đây thì trên nền cam sẽ tụt xuống dưới ngưỡng đọc
 * được. Vai trò vẫn được nói thành lời trong `aria-label`, nơi không tốn chỗ.
 */
function NameChoiceButton({
	name,
	students,
	isCurrent,
	isTop,
	selected,
	willOverwrite,
	onSelect,
}: NameChoiceButtonProps) {
	const roles = [
		isCurrent ? "đang dùng" : null,
		isTop ? "nhiều SV nhất" : null,
		students > 0 ? `${students} sinh viên trong file` : "không có trong file",
	].filter(Boolean);

	return (
		<Button
			type="button"
			size="sm"
			className={cn("h-8 gap-1", willOverwrite && "bg-amber-600 hover:bg-amber-700")}
			variant={selected ? "default" : "outline"}
			aria-label={`${name} — ${roles.join(", ")}`}
			onClick={onSelect}
		>
			{isTop && <Star className="h-3 w-3 shrink-0 fill-current" aria-hidden="true" />}
			<span className={cn(isCurrent && "font-bold")}>{name}</span>
			<span className="tabular-nums opacity-70" aria-hidden="true">
				{students}
			</span>
		</Button>
	);
}

/**
 * Admin tab: upload the TS2026_GiayBao `.xlsx`, preview validation results
 * (row counts, format errors, duplicate MaSV) without writing anything, then
 * decide what happens to each row before confirming.
 *
 * `MaSV` not yet in the DB is always inserted, no decision needed. `MaSV`
 * already in the DB defaults to SKIP — the real scenario is a corrected file
 * re-imported after reception staff have already worked a student's
 * paperwork, and a blind overwrite would silently erase that work. The
 * operator must explicitly choose "Cập nhật" per row (or "Cập nhật tất cả")
 * to let a conflicting row be written; anything left unset stays skipped.
 */
export default function StudentImport() {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<File | null>(null);
	const [previewing, setPreviewing] = useState(false);
	const [committing, setCommitting] = useState(false);
	const [preview, setPreview] = useState<StudentImportPreview | null>(null);
	const [previewError, setPreviewError] = useState<string | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [decisions, setDecisions] = useState<StudentImportDecisions>({});
	const [highSchoolNameDecisions, setHighSchoolNameDecisions] = useState<HighSchoolNameDecisions>(
		{},
	);
	const [lastResult, setLastResult] = useState<StudentImportCommitResult | null>(null);

	const resetForNewFile = () => {
		setPreview(null);
		setPreviewError(null);
		setDecisions({});
		setHighSchoolNameDecisions({});
		setLastResult(null);
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0] ?? null;
		resetForNewFile();
		setFile(selected);
		if (!selected) return;

		setPreviewing(true);
		try {
			const result = await studentImportApi.preview(selected);
			setPreview(result);
			// Every conflicting row starts as "skip" — the safe default. The
			// operator must actively flip a row (or "Cập nhật tất cả") to update it.
			setDecisions(Object.fromEntries(result.conflicts.map((c) => [c.MaSV, "skip" as const])));
			// Mọi mã trường cần duyệt tên khởi động với ứng viên nhiều SV nhất đã
			// CHỌN SẴN (options đã sắp giảm dần, phần tử đầu luôn là ứng viên đó) —
			// để admin duyệt nhanh, chỉ cần sửa những dòng muốn khác.
			setHighSchoolNameDecisions(
				Object.fromEntries(
					result.highSchoolNameConflicts.map((c) => [`${c.system}:${c.code}`, c.options[0].name]),
				),
			);
		} catch (error) {
			const message = getSafeErrorMessage(error);
			setPreviewError(message);
			Toast.error("Không đọc được file", message);
		} finally {
			setPreviewing(false);
		}
	};

	const canCommit =
		!!file &&
		!!preview &&
		preview.errors.length === 0 &&
		preview.duplicateMaSV.length === 0 &&
		preview.validRows > 0;

	const setRowDecision = (masv: string, decision: "skip" | "update") => {
		setDecisions((prev) => ({ ...prev, [masv]: decision }));
	};

	const setAllConflictDecisions = (decision: "skip" | "update") => {
		if (!preview) return;
		setDecisions(Object.fromEntries(preview.conflicts.map((c) => [c.MaSV, decision])));
	};

	const conflictCount = preview?.conflicts.length ?? 0;
	const updateCount = preview
		? preview.conflicts.filter((c) => decisions[c.MaSV] === "update").length
		: 0;
	const skipCount = conflictCount - updateCount;

	const setHighSchoolNameDecision = (system: string, code: string, name: string) => {
		setHighSchoolNameDecisions((prev) => ({ ...prev, [`${system}:${code}`]: name }));
	};

	/** "topOption": ứng viên nhiều SV nhất (mặc định). "currentName": giữ tên
	 * đang có trong danh mục — chỉ tác động những mã ĐÃ có tên, mã mới không có
	 * gì để "giữ" nên giữ nguyên lựa chọn đang chọn. */
	const setAllHighSchoolNameDecisions = (mode: "topOption" | "currentName") => {
		if (!preview) return;
		setHighSchoolNameDecisions((prev) =>
			Object.fromEntries(
				preview.highSchoolNameConflicts.map((c) => {
					const key = `${c.system}:${c.code}`;
					if (mode === "currentName" && c.currentName !== null) return [key, c.currentName];
					if (mode === "currentName") return [key, prev[key] ?? c.options[0].name];
					return [key, c.options[0].name];
				}),
			),
		);
	};

	const highSchoolNameConflictCount = preview?.highSchoolNameConflicts.length ?? 0;

	/**
	 * Số mã trường sắp bị đổi tên trong danh mục theo lựa chọn hiện tại. Đây là
	 * phần DUY NHẤT của lượt nhập có thể mất dữ liệu cũ — mã mới chỉ được thêm
	 * vào, không đè lên gì.
	 */
	const highSchoolRenameCount = preview
		? preview.highSchoolNameConflicts.filter((c) =>
				willRenameHighSchool(c, highSchoolNameDecisions),
			).length
		: 0;

	/** Không dòng sinh viên nào được ghi, nhưng danh mục trường vẫn đổi. */
	const renamesWithoutStudentWrites =
		highSchoolRenameCount > 0 && (preview?.insertCount ?? 0) === 0 && updateCount === 0;

	const handleCommit = async () => {
		if (!file) return;
		setCommitting(true);
		const promise = studentImportApi.commit(file, decisions, highSchoolNameDecisions);
		Toast.promise(promise, {
			loading: "Đang nhập dữ liệu...",
			success: "Đã nhập dữ liệu",
			error: "Không thể nhập dữ liệu",
		});
		try {
			const result = await promise;
			setLastResult(result);
			setConfirmOpen(false);
			setFile(null);
			setPreview(null);
			setPreviewError(null);
			setDecisions({});
			setHighSchoolNameDecisions({});
			if (fileInputRef.current) fileInputRef.current.value = "";
		} catch {
			// Toast.promise already surfaced the error toast; leave the dialog
			// open (and the file selected) so the operator can retry.
		} finally {
			setCommitting(false);
		}
	};

	return (
		<div className="space-y-6">
			<Card>
				<CardContent className="p-6 space-y-4">
					<div className="space-y-1">
						<h3 className="text-lg font-semibold text-foreground">
							Nhập dữ liệu sinh viên (.xlsx)
						</h3>
						<p className="text-sm text-muted-foreground">
							Chọn file đúng định dạng TS2026_GiayBao (Sheet1). File sẽ được kiểm tra trước — không
							có gì được ghi vào cơ sở dữ liệu cho đến khi bấm "Xác nhận nhập". Tên ngành không lấy
							từ file mà lấy theo mã ngành trong danh mục ngành.
						</p>
					</div>

					<div className="flex items-center gap-3">
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx"
							onChange={handleFileChange}
							disabled={previewing || committing}
							className="h-9 flex-1 text-sm file:mr-3 file:h-9 file:rounded-md file:border-0 file:bg-muted file:px-3 file:text-sm file:font-medium file:text-muted-foreground hover:file:bg-accent"
						/>
						{previewing && (
							<span className="flex items-center gap-2 text-sm text-muted-foreground">
								<Loader2 className="h-4 w-4 animate-spin" />
								Đang kiểm tra file...
							</span>
						)}
					</div>

					{previewError && (
						<div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-400">
							<AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
							<span>{previewError}</span>
						</div>
					)}

					{lastResult && (
						<div className="flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950 dark:text-green-400">
							<CheckCircle2 className="h-4 w-4 shrink-0" />
							Đã nhập xong: tạo mới {lastResult.inserted}, cập nhật {lastResult.updated}, bỏ qua{" "}
							{lastResult.skipped}.
						</div>
					)}

					{preview && !previewError && (
						<div className="space-y-4">
							<div className="flex flex-wrap items-center gap-3">
								<Badge variant="secondary">{preview.totalRows} dòng đọc được</Badge>
								<Badge variant={preview.validRows === preview.totalRows ? "default" : "secondary"}>
									{preview.validRows} dòng hợp lệ
								</Badge>
								{preview.errors.length > 0 && (
									<Badge variant="destructive">{preview.errors.length} lỗi</Badge>
								)}
								{preview.duplicateMaSV.length > 0 && (
									<Badge variant="destructive">{preview.duplicateMaSV.length} MaSV trùng lặp</Badge>
								)}
								{preview.insertCount > 0 && (
									<Badge variant="default">{preview.insertCount} MaSV mới → tạo mới</Badge>
								)}
								{conflictCount > 0 && (
									<Badge variant="secondary">
										{conflictCount} MaSV đã tồn tại → cần quyết định
									</Badge>
								)}
								{preview.unchangedCount > 0 && (
									<Badge variant="secondary">
										{preview.unchangedCount} MaSV không đổi → tự bỏ qua
									</Badge>
								)}
								{highSchoolNameConflictCount > 0 && (
									<Badge variant="secondary">
										{highSchoolNameConflictCount} mã trường THPT cần duyệt tên
									</Badge>
								)}
							</div>

							{preview.errors.length > 0 && (
								<div className="max-h-48 overflow-y-auto rounded-md border">
									<table className="w-full text-sm">
										<thead className="sticky top-0 bg-muted">
											<tr>
												<th className="px-3 py-2 text-left font-medium text-muted-foreground">
													Dòng
												</th>
												<th className="px-3 py-2 text-left font-medium text-muted-foreground">
													Lỗi
												</th>
											</tr>
										</thead>
										<tbody>
											{preview.errors.map((err, i) => (
												<tr key={i} className="border-t">
													<td className="px-3 py-2 text-muted-foreground">{err.row}</td>
													<td className="px-3 py-2 text-red-700 dark:text-red-400">
														{err.message}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}

							{preview.duplicateMaSV.length > 0 && (
								<p className="text-sm text-red-700 dark:text-red-400">
									MaSV trùng lặp trong file: {preview.duplicateMaSV.join(", ")}
								</p>
							)}

							{conflictCount > 0 && (
								<div className="space-y-2">
									<div className="flex items-center justify-between gap-3">
										<p className="text-sm text-muted-foreground">
											{updateCount} sẽ cập nhật, {skipCount} sẽ bỏ qua (mặc định bỏ qua để không ghi
											đè công việc tiếp nhận đã có).
										</p>
										<div className="flex items-center gap-2">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="h-8"
												onClick={() => setAllConflictDecisions("skip")}
											>
												Bỏ qua tất cả
											</Button>
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="h-8"
												onClick={() => setAllConflictDecisions("update")}
											>
												Cập nhật tất cả
											</Button>
										</div>
									</div>

									<div className="max-h-80 overflow-y-auto rounded-md border">
										<table className="w-full text-sm">
											<thead className="sticky top-0 bg-muted">
												<tr>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														MaSV
													</th>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Tên trong CSDL
													</th>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Tên trong file
													</th>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Cột thay đổi
													</th>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Quyết định
													</th>
												</tr>
											</thead>
											<tbody>
												{preview.conflicts.map((c) => {
													const decision = decisions[c.MaSV] ?? "skip";
													return (
														<tr key={c.MaSV} className="border-t align-top">
															<td className="px-3 py-2 font-mono text-foreground">{c.MaSV}</td>
															<td className="px-3 py-2 text-muted-foreground">
																{c.hoTenInDb ?? "—"}
															</td>
															<td className="px-3 py-2 text-muted-foreground">
																{c.hoTenInFile ?? "—"}
															</td>
															<td className="px-3 py-2 text-muted-foreground">
																{c.changedColumns.length === 0 ? (
																	<span className="text-muted-foreground">Không đổi</span>
																) : (
																	<div className="space-y-1">
																		{c.changedColumns.map((col) => (
																			<div key={col.dbColumn} className="leading-snug">
																				<span className="font-medium text-muted-foreground">
																					{col.xlsxHeader}:
																				</span>{" "}
																				<DiffText
																					oldText={col.oldValue ?? ""}
																					newText={col.newValue ?? ""}
																				/>
																				{/* Ô trống trong file XOÁ giá trị đang có. Không có nhãn thì
																				    dòng này chỉ còn chữ gạch bỏ rồi hết — trông giống lỗi hiển
																				    thị hơn là một lệnh xoá, đúng lúc người duyệt cần thấy rõ
																				    nhất. */}
																				{col.newValue === null && (
																					<span className="ml-1 rounded-[2px] bg-amber-100 px-1 text-[11px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
																						xoá trống
																					</span>
																				)}
																			</div>
																		))}
																	</div>
																)}
															</td>
															<td className="px-3 py-2">
																<div className="flex items-center gap-1.5">
																	<Button
																		type="button"
																		size="sm"
																		className="h-8"
																		variant={decision === "skip" ? "default" : "outline"}
																		onClick={() => setRowDecision(c.MaSV, "skip")}
																	>
																		Bỏ qua
																	</Button>
																	<Button
																		type="button"
																		size="sm"
																		className={cn(
																			"h-8",
																			decision === "update" && "bg-amber-600 hover:bg-amber-700",
																		)}
																		variant={decision === "update" ? "default" : "outline"}
																		onClick={() => setRowDecision(c.MaSV, "update")}
																	>
																		Cập nhật
																	</Button>
																</div>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{highSchoolNameConflictCount > 0 && (
								<div className="space-y-2">
									<div className="flex items-center justify-between gap-3">
										<p className="text-sm text-muted-foreground">
											{highSchoolNameConflictCount} mã trường THPT có nhiều cách viết tên hoặc khác
											tên đang có trong danh mục. Mặc định chọn sẵn tên nhiều sinh viên nhất — có
											thể ghi đè tên đang dùng, đổi lại dòng nào không muốn.
											<span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
												<span>
													<span className="font-bold text-muted-foreground">Đậm</span> = đang trong
													danh mục
												</span>
												<span className="flex items-center gap-1">
													<Star className="h-3 w-3 fill-current" aria-hidden="true" /> = nhiều SV
													nhất
												</span>
												<span>
													<span className="tabular-nums text-muted-foreground">Số</span> = số SV
													trong file viết vậy
												</span>
												<span>
													<span className="text-amber-700 dark:text-amber-400">Cam</span> = sẽ ghi
													đè danh mục
												</span>
											</span>
										</p>
										<div className="flex items-center gap-2">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="h-8"
												onClick={() => setAllHighSchoolNameDecisions("currentName")}
											>
												Giữ toàn bộ tên hiện tại
											</Button>
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="h-8"
												onClick={() => setAllHighSchoolNameDecisions("topOption")}
											>
												Chọn tên nhiều SV nhất
											</Button>
										</div>
									</div>

									<div className="max-h-80 overflow-y-auto rounded-md border">
										<table className="w-full text-sm">
											<thead className="sticky top-0 bg-muted">
												<tr>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Mã trường
													</th>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Tên đang dùng
													</th>
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Chọn tên ghi vào danh mục
													</th>
												</tr>
											</thead>
											<tbody>
												{preview.highSchoolNameConflicts.map((c) => {
													const key = `${c.system}:${c.code}`;
													const selected = selectedHighSchoolName(c, highSchoolNameDecisions);
													const willOverwrite = willRenameHighSchool(c, highSchoolNameDecisions);
													return (
														<tr key={key} className="border-t align-top">
															<td className="px-3 py-2 font-mono text-foreground">{c.code}</td>
															<td className="px-3 py-2 text-muted-foreground">
																{c.currentName === null ? (
																	<span className="text-muted-foreground">Mã mới</span>
																) : (
																	<span
																		className={cn(
																			willOverwrite &&
																				"font-medium text-amber-700 dark:text-amber-400",
																		)}
																	>
																		{c.currentName}{" "}
																		<span className="text-xs text-muted-foreground">
																			(đang dùng)
																		</span>
																	</span>
																)}
															</td>
															<td className="px-3 py-2">
																<div className="flex flex-wrap items-center gap-1.5">
																	{/* Ứng viên chỉ gom từ file, nên khi cả file mang cách viết
																	    khác danh mục thì KHÔNG có nút nào giữ được tên hiện tại —
																	    dòng đó chỉ còn cách đổi tên. Nút này là quyết định "giữ
																	    nguyên" cho riêng dòng đó; nút hàng loạt không thay thế
																	    được vì nó áp cho mọi dòng. */}
																	{c.currentName !== null &&
																		!c.options.some((o) => o.name === c.currentName) && (
																			<NameChoiceButton
																				name={c.currentName}
																				students={0}
																				isCurrent
																				isTop={false}
																				selected={selected === c.currentName}
																				willOverwrite={false}
																				onSelect={() =>
																					setHighSchoolNameDecision(
																						c.system,
																						c.code,
																						c.currentName as string,
																					)
																				}
																			/>
																		)}
																	{c.options.map((option, optionIndex) => (
																		<NameChoiceButton
																			key={option.name}
																			name={option.name}
																			students={option.students}
																			isCurrent={option.name === c.currentName}
																			// Sao chỉ gắn khi có một ứng viên THẮNG hẳn. Hoà phiếu thì
																			// `sortedOptions` xếp theo bảng chữ cái, nên "nhiều SV nhất"
																			// sẽ là lời nói dối — và biến thể lệch dấu hoà nhau liên tục
																			// ở cohort nhỏ (2-2, 1-1).
																			isTop={
																				optionIndex === 0 &&
																				c.options.length > 1 &&
																				c.options[0].students > c.options[1].students
																			}
																			selected={selected === option.name}
																			// Dùng CHUNG một định nghĩa với dòng cảnh báo bên dưới. Tính
																			// riêng ở đây thì mã MỚI (`currentName` null) cũng bị tô cam,
																			// trong khi chẳng có gì để ghi đè.
																			willOverwrite={willOverwrite && selected === option.name}
																			onSelect={() =>
																				setHighSchoolNameDecision(c.system, c.code, option.name)
																			}
																		/>
																	))}
																</div>
																{/* Diff nằm ở ĐÂY chứ không trong nhãn nút: nhãn nút là tên
																    trường admin đang chọn, chèn ký tự của tên cũ vào đó sẽ
																    tạo ra một tên không tồn tại. Ở đây thì ngược lại — câu
																    hỏi đúng là "đổi cái gì so với tên đang dùng", và 81 mã
																    trên production lệch nhau vài ký tự, đọc trơn không thấy. */}
																{willOverwrite && c.currentName !== null && (
																	<p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
																		<AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
																		<span>
																			Sẽ thay tên đang dùng:{" "}
																			<DiffText oldText={c.currentName} newText={selected} />
																		</span>
																	</p>
																)}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{canCommit && (
								<div className="flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950 dark:text-green-400">
									<CheckCircle2 className="h-4 w-4 shrink-0" />
									File hợp lệ, sẵn sàng nhập {preview.validRows} sinh viên.
								</div>
							)}

							<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
								<Button disabled={!canCommit} onClick={() => setConfirmOpen(true)}>
									<Upload className="h-4 w-4" />
									Xác nhận nhập
								</Button>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>Xác nhận nhập dữ liệu sinh viên</AlertDialogTitle>
										<AlertDialogDescription>
											Sẽ tạo mới {preview.insertCount} sinh viên, cập nhật {updateCount} và bỏ qua{" "}
											{skipCount} sinh viên đã tồn tại theo lựa chọn ở trên. Các trường thuộc công
											việc tiếp nhận (tình trạng học phí, đã tiếp nhận, giấy tờ đã nhận) luôn được
											giữ nguyên, kể cả với dòng chọn cập nhật.
											{/* Lượt nhập ghi vào HAI bảng, nhưng hộp này từng chỉ kể bảng student —
											    rồi dặn sao lưu đúng cái bảng không bị ảnh hưởng. Danh mục trường đi
											    theo mọi dòng hợp lệ, không đi theo lựa chọn bỏ qua/cập nhật, nên
											    "bỏ qua tất cả" KHÔNG có nghĩa là không ghi gì. */}
											<span className="mt-2 block">
												Danh mục trường THPT đi theo mọi dòng hợp lệ trong file, không phụ thuộc lựa
												chọn bỏ qua/cập nhật ở trên: mã chưa có sẽ được thêm
												{highSchoolRenameCount > 0
													? `, và ${highSchoolRenameCount} mã sẽ bị đổi tên`
													: ""}
												.
											</span>
											{renamesWithoutStudentWrites && (
												<span className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-50 p-2 font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
													<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
													<span>
														Không sinh viên nào được ghi, nhưng {highSchoolRenameCount} mã trường
														vẫn bị đổi tên trong danh mục.
													</span>
												</span>
											)}
											<span className="mt-2 block">
												Thao tác không thể hoàn tác tự động — đảm bảo đã sao lưu bảng student và
												high_school trước khi tiếp tục.
											</span>
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel disabled={committing}>Hủy</AlertDialogCancel>
										<AlertDialogAction
											disabled={committing}
											onClick={(e) => {
												e.preventDefault();
												handleCommit();
											}}
										>
											{committing ? (
												<>
													<Loader2 className="h-4 w-4 animate-spin" />
													Đang nhập...
												</>
											) : (
												"Xác nhận nhập"
											)}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
